import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import {
  fetchFilterOptions,
  fetchPackages,
  fetchStats,
  fetchTraces,
} from "./api";
import {
  LevelDot,
  Mono,
  PHASE_ORDER,
  PhasePill,
  ScanExhaustedNotice,
  StateBlock,
  TRACE_PHASE_LABELS,
  formatPhase,
  formatRelative,
} from "./shared";
import FeatureFlagsTab from "./FeatureFlagsTab";
import { isLocalDashboard } from "../OrgUsageAnalytics/api";
import "./NewfiSplitEdocs.css";

const ENV_KEY = "nse-env";
const PAGE_SIZE = 50;
const TRACE_PAGE_SIZE = 100;

const EMPTY_FILTERS = {
  organizationId: "",
  phase: "",
  loanNumber: "",
  meridianLinkDocumentId: "",
  packageKind: "",
  packageChannel: "",
  hasErrors: "",
  search: "",
};

const EMPTY_TRACE_FILTERS = {
  organizationId: "",
  loanNumber: "",
  level: "",
  phase: "",
  code: "",
  search: "",
};

/**
 * Strip the blanks before they reach the query string.
 *
 * `hasErrors` is the one that matters: the backend treats absent as "do not
 * filter" and `false` as "only packages with no errors". Sending an empty
 * string would collapse that distinction.
 */
function toQuery(filters, extra = {}) {
  const params = {};
  Object.entries({ ...filters, ...extra }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  });
  return params;
}

/**
 * The counts row.
 *
 * Renders a placeholder rather than nothing while the counts are in flight.
 * Returning `null` made a slow `/stats` and a broken one look identical — the
 * strip simply was not there — which is why "the metrics never appear" read as
 * a missing feature rather than as a pending request.
 */
function StatsStrip({
  stats,
  loading,
  error,
  onRetry,
  activePhase,
  onPickPhase,
}) {
  if (!stats) {
    return (
      <div className="nse-stats nse-stats--placeholder" aria-live="polite">
        {error ? (
          <>
            <span className="nse-stats__note">Counts unavailable — {error}</span>
            <button type="button" className="nse-btn" onClick={onRetry}>
              Retry counts
            </button>
          </>
        ) : (
          <span className="nse-stats__note">
            {loading ? "Counting packages…" : "No counts yet."}
          </span>
        )}
      </div>
    );
  }
  const byPhase = stats.byPhase || {};
  return (
    <div className="nse-stats">
      <button
        type="button"
        className={`nse-stat ${!activePhase ? "is-active" : ""}`}
        onClick={() => onPickPhase("")}
      >
        <span className="nse-stat__value">
          {(stats.total || 0).toLocaleString()}
        </span>
        <span className="nse-stat__label">All packages</span>
      </button>
      {PHASE_ORDER.filter((phase) => byPhase[phase] > 0 || phase === "ready").map(
        (phase) => (
          <button
            key={phase}
            type="button"
            className={`nse-stat nse-stat--${phase} ${
              activePhase === phase ? "is-active" : ""
            }`}
            onClick={() => onPickPhase(activePhase === phase ? "" : phase)}
          >
            <span className="nse-stat__value">
              {(byPhase[phase] || 0).toLocaleString()}
            </span>
            <span className="nse-stat__label">{formatPhase(phase)}</span>
          </button>
        ),
      )}
      <div className="nse-stat nse-stat--errors" aria-live="polite">
        <span className="nse-stat__value">
          {(stats.withErrors || 0).toLocaleString()}
        </span>
        <span className="nse-stat__label">With errors</span>
      </div>
    </div>
  );
}

function PackagesTab({
  env,
  filterOptions,
  onOpen,
  stats,
  statsLoading,
  statsError,
  onRetryStats,
}) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [packages, setPackages] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [scanExhausted, setScanExhausted] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (activeFilters, { append = false, fromCursor = null } = {}) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await fetchPackages(
          toQuery(activeFilters, { limit: PAGE_SIZE, cursor: fromCursor }),
          env,
        );
        if (!res.success) {
          throw new Error(res.reason || "Failed to load packages.");
        }
        const rows = Array.isArray(res.packages) ? res.packages : [];
        setPackages((current) => (append ? [...current, ...rows] : rows));
        setCursor(res.nextCursor || null);
        setHasMore(Boolean(res.hasMore));
        setScanExhausted(Boolean(res.scanExhausted));
        setScanned((current) =>
          append ? current + (res.scanned || 0) : res.scanned || 0,
        );
      } catch (err) {
        setError(err.message);
        if (append) {
          toast.error(err.message);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [env],
  );

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  const apply = (next) => {
    setFilters(next);
    setApplied(next);
  };

  const setField = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const pickPhase = (phase) => apply({ ...filters, phase });

  return (
    <>
      <StatsStrip
        stats={stats}
        loading={statsLoading}
        error={statsError}
        onRetry={onRetryStats}
        activePhase={applied.phase}
        onPickPhase={pickPhase}
      />

      <form
        className="nse-filters"
        onSubmit={(event) => {
          event.preventDefault();
          apply(filters);
        }}
      >
        <input
          className="nse-input"
          placeholder="Organization ID"
          value={filters.organizationId}
          onChange={(event) => setField("organizationId", event.target.value)}
        />
        <input
          className="nse-input"
          placeholder="Loan number"
          value={filters.loanNumber}
          onChange={(event) => setField("loanNumber", event.target.value)}
        />
        <input
          className="nse-input"
          placeholder="MeridianLink document ID"
          value={filters.meridianLinkDocumentId}
          onChange={(event) =>
            setField("meridianLinkDocumentId", event.target.value)
          }
        />
        <select
          className="nse-select"
          value={filters.phase}
          onChange={(event) => setField("phase", event.target.value)}
        >
          <option value="">Any phase</option>
          {(filterOptions?.phases || PHASE_ORDER).map((phase) => (
            <option key={phase} value={phase}>
              {formatPhase(phase)}
            </option>
          ))}
        </select>
        <select
          className="nse-select"
          value={filters.packageKind}
          onChange={(event) => setField("packageKind", event.target.value)}
        >
          <option value="">Any package kind</option>
          {(filterOptions?.packageKinds || []).map((kind) => (
            <option key={kind} value={kind}>
              {kind}
            </option>
          ))}
        </select>
        <select
          className="nse-select"
          value={filters.packageChannel}
          onChange={(event) => setField("packageChannel", event.target.value)}
        >
          <option value="">Any channel</option>
          {(filterOptions?.packageChannels || []).map((channel) => (
            <option key={channel} value={channel}>
              {channel}
            </option>
          ))}
        </select>
        <select
          className="nse-select"
          value={filters.hasErrors}
          onChange={(event) => setField("hasErrors", event.target.value)}
        >
          <option value="">Errors: any</option>
          <option value="true">Has errors</option>
          <option value="false">No errors</option>
        </select>
        <input
          className="nse-input nse-input--grow"
          placeholder="Search loan, doc type, last message…"
          value={filters.search}
          onChange={(event) => setField("search", event.target.value)}
        />
        <button className="nse-btn nse-btn--primary" type="submit">
          Apply
        </button>
        <button
          className="nse-btn"
          type="button"
          onClick={() => apply(EMPTY_FILTERS)}
        >
          Reset
        </button>
      </form>

      {scanExhausted && (
        <ScanExhaustedNotice
          scanned={scanned}
          onLoadMore={() => load(applied, { append: true, fromCursor: cursor })}
        />
      )}

      <StateBlock
        loading={loading}
        error={error}
        empty={packages.length === 0}
        emptyText="No packages match these filters."
      >
        <div className="nse-table-scroll">
          <table className="nse-table">
            <thead>
              <tr>
                <th>Loan</th>
                <th>Package</th>
                <th>Phase</th>
                <th className="nse-num">Splits</th>
                <th>Push</th>
                <th className="nse-num">Traces</th>
                <th>Last event</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((row) => (
                <tr
                  key={row.recordId}
                  className="nse-row"
                  onClick={() => onOpen(row.recordId)}
                >
                  <td>
                    <span className="nse-strong">{row.loanNumber || "—"}</span>
                    <span className="nse-cell-note">
                      <Mono value={row.organizationId} truncate={12} />
                    </span>
                  </td>
                  <td>
                    <span className="nse-doc-type">{row.docType || "—"}</span>
                    <span className="nse-cell-note">
                      {row.packageChannel || "unknown channel"}
                      {row.origin === "writeback" && (
                        <span className="nse-tag nse-tag--external">
                          external
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    <PhasePill phase={row.phase} />
                    {row.outOfScopeReason && (
                      <span className="nse-cell-note" title={row.outOfScopeReason}>
                        {row.outOfScopeReason}
                      </span>
                    )}
                    {row.abandonedReason && (
                      <span className="nse-cell-note" title={row.abandonedReason}>
                        {row.abandonedReason}
                      </span>
                    )}
                  </td>
                  <td className="nse-num">{row.splitCount ?? "—"}</td>
                  <td>
                    {row.pushOutcome ? (
                      <span
                        className={`nse-tag nse-tag--${
                          row.pushOutcome.startsWith("applied")
                            ? "ok"
                            : "warn"
                        }`}
                      >
                        {row.pushOutcome}
                      </span>
                    ) : (
                      <span className="nse-muted">—</span>
                    )}
                    {row.uploadedSplitCount != null && (
                      <span className="nse-cell-note">
                        {row.uploadedSplitCount} uploaded
                        {row.failedSplitCount ? `, ${row.failedSplitCount} failed` : ""}
                      </span>
                    )}
                  </td>
                  <td className="nse-num">
                    {row.traceCount || 0}
                    {row.traceErrorCount > 0 && (
                      <span className="nse-error-count">
                        {row.traceErrorCount} err
                      </span>
                    )}
                  </td>
                  <td className="nse-last-event">
                    <LevelDot level={row.lastTraceLevel} />
                    <span title={row.lastTraceMessage || ""}>
                      {row.lastTraceMessage || "No traces yet"}
                    </span>
                  </td>
                  <td>{formatRelative(row.updatedAt || row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </StateBlock>

      {hasMore && !loading && (
        <div className="nse-load-more">
          <button
            type="button"
            className="nse-btn"
            disabled={loadingMore}
            onClick={() => load(applied, { append: true, fromCursor: cursor })}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </>
  );
}

function TracesTab({ env, filterOptions, onOpen }) {
  const [filters, setFilters] = useState({
    ...EMPTY_TRACE_FILTERS,
    level: "error",
  });
  const [applied, setApplied] = useState({
    ...EMPTY_TRACE_FILTERS,
    level: "error",
  });
  const [traces, setTraces] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [scanExhausted, setScanExhausted] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (activeFilters, { append = false, fromCursor = null } = {}) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await fetchTraces(
          toQuery(activeFilters, {
            limit: TRACE_PAGE_SIZE,
            cursor: fromCursor,
          }),
          env,
        );
        if (!res.success) {
          throw new Error(res.reason || "Failed to load traces.");
        }
        const rows = Array.isArray(res.traces) ? res.traces : [];
        setTraces((current) => (append ? [...current, ...rows] : rows));
        setCursor(res.nextCursor || null);
        setHasMore(Boolean(res.hasMore));
        setScanExhausted(Boolean(res.scanExhausted));
        setScanned((current) =>
          append ? current + (res.scanned || 0) : res.scanned || 0,
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [env],
  );

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  const setField = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));

  return (
    <>
      <p className="nse-tab-intro">
        Every trace across every package, newest first. This is the view for
        &ldquo;what is failing right now&rdquo; — the per-package timeline
        cannot answer that.
      </p>

      <form
        className="nse-filters"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(filters);
        }}
      >
        <input
          className="nse-input"
          placeholder="Organization ID"
          value={filters.organizationId}
          onChange={(event) => setField("organizationId", event.target.value)}
        />
        <input
          className="nse-input"
          placeholder="Loan number"
          value={filters.loanNumber}
          onChange={(event) => setField("loanNumber", event.target.value)}
        />
        <select
          className="nse-select"
          value={filters.level}
          onChange={(event) => setField("level", event.target.value)}
        >
          <option value="">Any level</option>
          {(filterOptions?.traceLevels || ["info", "warn", "error"]).map(
            (level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ),
          )}
        </select>
        <select
          className="nse-select"
          value={filters.phase}
          onChange={(event) => setField("phase", event.target.value)}
        >
          <option value="">Any stage</option>
          {(filterOptions?.tracePhases || Object.keys(TRACE_PHASE_LABELS)).map(
            (phase) => (
              <option key={phase} value={phase}>
                {TRACE_PHASE_LABELS[phase] || phase}
              </option>
            ),
          )}
        </select>
        <select
          className="nse-select nse-select--wide"
          value={filters.code}
          onChange={(event) => setField("code", event.target.value)}
        >
          <option value="">Any event</option>
          {(filterOptions?.traceCodes || []).map((entry) => (
            <option key={entry.code} value={entry.code} title={entry.description}>
              {entry.code} — {entry.description}
            </option>
          ))}
        </select>
        <input
          className="nse-input nse-input--grow"
          placeholder="Search message…"
          value={filters.search}
          onChange={(event) => setField("search", event.target.value)}
        />
        <button className="nse-btn nse-btn--primary" type="submit">
          Apply
        </button>
      </form>

      {scanExhausted && (
        <ScanExhaustedNotice
          scanned={scanned}
          onLoadMore={() => load(applied, { append: true, fromCursor: cursor })}
        />
      )}

      <StateBlock
        loading={loading}
        error={error}
        empty={traces.length === 0}
        emptyText="No traces match these filters."
      >
        <div className="nse-table-scroll">
          <table className="nse-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Stage</th>
                <th>Loan</th>
                <th>Event</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {traces.map((trace) => (
                <tr
                  key={`${trace.recordId}-${trace.id}`}
                  className="nse-row"
                  onClick={() => onOpen(trace.recordId)}
                >
                  <td title={new Date(trace.at || 0).toLocaleString()}>
                    {formatRelative(trace.at)}
                  </td>
                  <td>
                    <span className="nse-tag">
                      {TRACE_PHASE_LABELS[trace.phase] || trace.phase}
                    </span>
                  </td>
                  <td>
                    <span className="nse-strong">{trace.loanNumber || "—"}</span>
                    <span className="nse-cell-note">
                      <Mono value={trace.meridianLinkDocumentId} truncate={14} />
                    </span>
                  </td>
                  <td>
                    <LevelDot level={trace.level} />
                    <Mono value={trace.code} />
                  </td>
                  <td className="nse-message">{trace.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </StateBlock>

      {hasMore && !loading && (
        <div className="nse-load-more">
          <button
            type="button"
            className="nse-btn"
            disabled={loadingMore}
            onClick={() => load(applied, { append: true, fromCursor: cursor })}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </>
  );
}

export default function NewfiSplitEdocsPage({ onReady }) {
  const [theme] = useDarkMode();
  const history = useHistory();

  // "local" only exists while the dashboard itself is served from this machine;
  // a persisted "local" carried onto the deployed dashboard has no backend to
  // talk to, so it degrades to prod rather than failing every request.
  const [env, setEnv] = useState(() => {
    const stored = localStorage.getItem(ENV_KEY) || "prod";
    return stored === "local" && !isLocalDashboard() ? "prod" : stored;
  });
  const [tab, setTab] = useState("packages");
  const [filterOptions, setFilterOptions] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Retrying the counts must not throw away a loaded, filtered, paginated
  // table, so it gets its own token rather than reusing `reloadToken` — which
  // remounts every tab.
  const [statsToken, setStatsToken] = useState(0);
  const readyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetchFilterOptions(env)
      .then((res) => {
        if (!cancelled && res?.success) {
          setFilterOptions(res.filters);
        }
      })
      .catch(() => {
        // Non-fatal: the dropdowns fall back to their built-in lists. The page
        // itself still works, and the table below will surface the real error.
      })
      .finally(() => {
        if (!readyRef.current) {
          readyRef.current = true;
          onReady?.();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [env, onReady]);

  useEffect(() => {
    let cancelled = false;
    setStatsError(null);
    setStatsLoading(true);
    fetchStats({}, env)
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (res?.success) {
          setStats(res.stats);
          return;
        }
        // A 200 that is not a success is still a failure, and silently keeping
        // the placeholder on screen forever is how this looked like a feature
        // that had never been built.
        setStats(null);
        setStatsError(res?.reason || "The counts endpoint returned no stats.");
      })
      .catch((err) => {
        if (!cancelled) {
          setStats(null);
          setStatsError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStatsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [env, reloadToken, statsToken]);

  const onEnvChange = (next) => {
    localStorage.setItem(ENV_KEY, next);
    setEnv(next);
    setStats(null);
  };

  // A package opens in its own tab rather than replacing this view: the list is
  // a triage surface, and throwing away a filtered, paginated, scrolled result
  // set just to read one package's trail is the opposite of what triage needs.
  // createHref (not a hand-built string) so the URL stays correct if the router
  // ever gains a basename.
  const openPackage = (recordId) => {
    if (!recordId) {
      return;
    }
    const href = history.createHref({
      pathname: `/newfi-split-edocs/${encodeURIComponent(recordId)}`,
      search: `?env=${env}`,
    });
    window.open(href, "_blank", "noopener,noreferrer");
  };

  // Remounts the active tab, which is the simplest correct refresh: every tab
  // owns its own cursor, and a stale cursor pointing into a re-ordered result
  // set is worse than starting the page over.
  const refresh = () => setReloadToken((token) => token + 1);

  const tabs = useMemo(
    () => [
      { key: "packages", label: "Packages" },
      { key: "traces", label: "All traces" },
      { key: "flags", label: "Feature flags" },
    ],
    [],
  );

  return (
    <div className="nse-page" data-theme={theme}>
      <ToastContainer position="bottom-right" theme={theme} />

      <div className="nse-header">
        <div>
          <h1 className="nse-title">Split eDocs</h1>
          <p className="nse-subtitle">
            The Newfi indexing pipeline, one row per MeridianLink package.
            Open a package to see every decision it made and every document it
            pushed back.
          </p>
        </div>
        <div className="nse-controls">
          <select
            className="nse-select"
            value={env}
            onChange={(event) => onEnvChange(event.target.value)}
          >
            <option value="prod">Prod</option>
            <option value="dev">Dev</option>
            {isLocalDashboard() && (
              <option value="local">Local backend</option>
            )}
          </select>
          <button type="button" className="nse-btn" onClick={refresh}>
            Refresh
          </button>
        </div>
      </div>

      {env === "local" && (
        <div className="nse-notice">
          <strong>Local backend.</strong> Requests go to{" "}
          <code>localhost:8080</code> first (<code>npm run dev:webserver</code>),
          then the Functions emulator on <code>localhost:5003</code>. Which
          Firestore you are reading is decided by the local backend&rsquo;s own{" "}
          <code>functions/.env</code>, not by this dropdown.
        </div>
      )}

      {statsError && statsError.includes("FIRESTORE_INDEX_MISSING") && (
        <div className="nse-notice nse-notice--warn">
          <strong>Counts unavailable.</strong> The per-phase counts need a
          Firestore index that is not deployed to this environment yet.
        </div>
      )}

      <div className="nse-tabs" role="tablist">
        {tabs.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={`nse-tab ${tab === entry.key ? "is-active" : ""}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === "packages" && (
        <PackagesTab
          key={`packages-${env}-${reloadToken}`}
          env={env}
          filterOptions={filterOptions}
          onOpen={openPackage}
          stats={stats}
          statsLoading={statsLoading}
          statsError={statsError}
          onRetryStats={() => setStatsToken((token) => token + 1)}
        />
      )}
      {tab === "traces" && (
        <TracesTab
          key={`traces-${env}-${reloadToken}`}
          env={env}
          filterOptions={filterOptions}
          onOpen={openPackage}
        />
      )}
      {tab === "flags" && (
        <FeatureFlagsTab key={`flags-${env}-${reloadToken}`} env={env} />
      )}
    </div>
  );
}
